import { useEffect, useLayoutEffect, useRef } from 'react'

// Drops focus so an aria-hidden pass (MUI's ModalManager, Base UI's
// markOthers) doesn't land on an ancestor of the focused element — Chrome
// warns "Blocked aria-hidden ... descendant retained focus" when it does.
export function blurActiveElement() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
}

// Modals apply aria-hidden to the rest of the page in a passive effect, while
// focus only moves into the popup later. A layout effect blurs the trigger
// after the modal has registered its mount but before any passive effect
// applies aria-hidden.
export const useBlurActiveElementOnOpen = (open: boolean) => {
  useLayoutEffect(() => {
    if (open) {
      blurActiveElement()
    }
  }, [open])
}

// When using MUI dialogs, the active element may be under aria-hidden="true".
// When open changes to false, MUI sets aria-hidden="true" on the drawer root, but a
// child element (button, input, etc.) still holds focus.
export const useBlurActiveElementOnExit = (open: boolean) => {
  const wasOpen = useRef(open)

  useEffect(() => {
    if (wasOpen.current && !open) {
      blurActiveElement()
    }

    wasOpen.current = open
  }, [open])
}

// Silences Chrome's "Blocked aria-hidden ... descendant retained focus" dev
// warning for shared modal wrappers — blurs on both the open and close passes.
export const useBlurActiveElementOnOpenChange = (open: boolean) => {
  useBlurActiveElementOnOpen(open)
  useBlurActiveElementOnExit(open)
}
